// src/lib/firebase.js
// ═══════════════════════════════════════════════════════════════════
//  STRUCTURE FIRESTORE :
//
//  users/{uid}                    ← doc PLAT (pas de sous-collection)
//    email, name, alertDays, lastAlert
//
//  houses/{houseId}               ← doc maison
//    name, ownerId, members: [uid, ...]
//    └─ inventory/{itemId}        ← sous-collection produits
//         name, emoji, cat, qty, unit, expiry, note, barcode, createdAt
// ═══════════════════════════════════════════════════════════════════

import { initializeApp }                               from 'firebase/app';
import { getFirestore, doc, collection, getDoc, setDoc,
         addDoc, updateDoc, deleteDoc, onSnapshot,
         query, orderBy, serverTimestamp, where,
         getDocs }                                      from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable }                 from 'firebase/functions';
import { generateUniqueInviteCode } from '../components/inviteCode';

// ⚠️  REMPLACEZ PAR VOS PROPRES CLÉS FIREBASE
const firebaseConfig = {
  apiKey:            "AIzaSyDYO_ov6Y5i29xNiwpkynLClTmSAhC-QHI",
  authDomain:        "wltm.fr",
  projectId:         "mareserve-4031b",
  storageBucket:     "mareserve-4031b.firebasestorage.app",
  messagingSenderId: "543646968038",
  appId:             "1:543646968038:web:533a265fe63bffce1a7327",
};

const app       = initializeApp(firebaseConfig);
export const db  = getFirestore(app);
export const auth = getAuth(app);
const fns        = getFunctions(app, 'europe-west1');

// ─── Auth ─────────────────────────────────────────────────────────
export const signInAnon = () => signInAnonymously(auth);
export const onAuth     = (cb) => onAuthStateChanged(auth, cb);

// ─── Users (doc plat : users/{uid}) ──────────────────────────────
export const userDoc = (uid) => doc(db, 'users', uid);

export const getUserData = async (uid) => {
  const snap = await getDoc(userDoc(uid));
  return snap.exists() ? snap.data() : null;
};

export const saveUserData = (uid, data) =>
  setDoc(userDoc(uid), data, { merge: true });

export const subscribeUser = (uid, cb) =>
  onSnapshot(userDoc(uid), snap => cb(snap.exists() ? snap.data() : null));

// ─── Houses (houses/{houseId}) ────────────────────────────────────
export const houseDoc   = (houseId) => doc(db, 'houses', houseId);
export const housesCol  = ()        => collection(db, 'houses');

export const createHouse = async (uid, name) => {
  const inviteCode = await generateUniqueInviteCode();
  const ref = await addDoc(housesCol(), {
    name,
    ownerId:   uid,
    members:   [uid],
    createdAt: serverTimestamp(),
    inviteCode,
  });
  return ref.id;
};

export const getHousesForUser = async (uid) => {
  const q    = query(housesCol(), where('members', 'array-contains', uid));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const subscribeHousesForUser = (uid, cb) => {
  const q = query(housesCol(), where('members', 'array-contains', uid));
  return onSnapshot(q, snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
};

export const updateHouse = (houseId, data) =>
  updateDoc(houseDoc(houseId), data);

export const deleteHouseDoc = (houseId) =>
  deleteDoc(houseDoc(houseId));

// ─── Inventory (houses/{houseId}/inventory/{itemId}) ─────────────
export const inventoryCol = (houseId) =>
  collection(db, 'houses', houseId, 'inventory');

export const inventoryQuery = (houseId) =>
  query(inventoryCol(houseId), orderBy('createdAt', 'desc'));

export const addItem = (houseId, data) =>
  addDoc(inventoryCol(houseId), { ...data, createdAt: serverTimestamp() });

export const updateItem = (houseId, itemId, data) =>
  updateDoc(doc(db, 'houses', houseId, 'inventory', itemId), data);

export const deleteItem = (houseId, itemId) =>
  deleteDoc(doc(db, 'houses', houseId, 'inventory', itemId));

export const subscribeInventory = (houseId, cb) =>
  onSnapshot(inventoryQuery(houseId), snap =>
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

// ─── MailerSend via Firebase Function ────────────────────────────
export const callSendExpiryAlert = (houseId) =>
  httpsCallable(fns, 'sendExpiryAlert')({ houseId });
